import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, Truck } from "lucide-react";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";
import { WHATSAPP_NUMBER } from "@/lib/whatsapp";

const STAGES = [
  { key: "pickup_pending", label: "Packed & awaiting pickup" },
  { key: "in_transit", label: "In transit" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
];
const ORDER = { processing: 0, pickup_pending: 0, picked_up: 1, in_transit: 1, delivery_issue: 2, out_for_delivery: 2, delivered: 3 };
const PROBLEM = { rto: "Returning to us", returned: "Returned to us", cancelled: "Cancelled", delivery_issue: "Delivery attempt unsuccessful" };

const fmt = (v) => {
  if (!v) return "";
  const d = new Date(typeof v === "number" ? v * 1000 : String(v).replace(" ", "T"));
  return isNaN(d) ? String(v) : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

const Shipment = ({ s }) => {
  const problem = PROBLEM[s.status_key];
  const cur = ORDER[s.status_key] ?? 0;
  return (
    <div className="mt-6 pt-5 border-t border-black/5" data-testid={`track-shipment-${s.awb}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <p className="text-sm flex items-center gap-2"><Truck className="w-4 h-4" /> {s.courier || "Courier"} · <span className="font-mono tracking-wider">{s.awb}</span></p>
        <p className={`text-xs uppercase tracking-[0.18em] px-2 py-1 border ${problem ? "border-rose-300 text-rose-700 bg-rose-50" : "border-emerald-300 text-emerald-800 bg-emerald-50"}`}>
          {problem || s.status_label}
        </p>
      </div>
      {s.items?.length > 0 && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{s.items.join(" · ")}</p>}
      {s.etd && !problem && s.status_key !== "delivered" && <p className="text-sm text-neutral-600 mt-2">Expected delivery: <strong>{s.etd}</strong></p>}
      {!problem && (
        <ol className="mt-5">
          {STAGES.map((st, i) => {
            const done = i < cur || s.status_key === "delivered";
            const current = i === cur && s.status_key !== "delivered";
            return (
              <li key={st.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : current ? <Loader2 className="w-5 h-5 text-black" /> : <Circle className="w-5 h-5 text-neutral-300" />}
                  {i < STAGES.length - 1 && <span className={`w-px flex-1 min-h-[22px] ${done ? "bg-emerald-600" : "bg-neutral-200"}`} />}
                </div>
                <p className={`pb-4 text-sm ${current ? "font-semibold text-black" : done ? "text-neutral-800" : "text-neutral-400"}`}>{st.label}</p>
              </li>
            );
          })}
        </ol>
      )}
      {s.events?.length > 0 && (
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer text-neutral-600">Courier updates ({s.events.length})</summary>
          <ul className="mt-3 space-y-3">
            {s.events.map((e, i) => (
              <li key={i} className="border-l-2 border-neutral-200 pl-3">
                <p className="text-neutral-800">{e.activity || e.status}</p>
                <p className="text-xs text-neutral-500">{[e.location, fmt(e.date)].filter(Boolean).join(" · ")}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
      {s.track_url && (
        <a href={s.track_url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-xs uppercase tracking-[0.18em] underline underline-offset-4">Open courier tracking page</a>
      )}
    </div>
  );
};

const TrackOrder = () => {
  const [mode, setMode] = useState("phone");
  const [name, setName] = useState("");
  const [pincode, setPincode] = useState("");
  const [phone, setPhone] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true); setData(null);
    try {
      const res = await fetch(`${API}/orders/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mode === "phone"
            ? { phone: phone.trim(), order_number: orderNo.trim() || null }
            : { name: name.trim(), pincode: pincode.trim() }
        ),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Please check the mobile number.");
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Track your order — Luxsova" description="Track your Luxsova order with your mobile number." />
      <div className="lux-container py-12 md:py-16 max-w-2xl" data-testid="track-order-page">
        <p className="overline text-neutral-500 mb-2">Customer care</p>
        <h1 className="text-3xl md:text-4xl font-semibold mb-3">Track your order</h1>
        <p className="text-neutral-600 mb-8">Enter the mobile number you used when you ordered — or your name and pincode. We'll show where your parcel is.</p>
        <div className="border border-black/10 p-5 md:p-6 bg-white">
          <div className="flex gap-2 mb-4 text-xs uppercase tracking-[0.18em]" role="tablist">
            {[["phone", "Mobile number"], ["name", "Name & pincode"]].map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => { setMode(k); setError(""); setData(null); }}
                className={`px-3 py-2 border ${mode === k ? "bg-black text-white border-black" : "border-black/20 text-neutral-600"}`} data-testid={`track-mode-${k}`}>{l}</button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "phone" ? (
              <>
                <input
                  className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                  placeholder="Mobile number (10 digits)" inputMode="tel" autoComplete="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="track-phone"
                />
                <input
                  className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                  placeholder="Order number (optional)" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} data-testid="track-order-no"
                />
              </>
            ) : (
              <>
                <input
                  className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                  placeholder="Full name (as on your order)" autoComplete="name"
                  value={name} onChange={(e) => setName(e.target.value)} required data-testid="track-name"
                />
                <input
                  className="w-full border-b border-black/20 py-2.5 outline-none focus:border-black bg-transparent"
                  placeholder="Delivery pincode (6 digits)" inputMode="numeric" autoComplete="postal-code" maxLength={6}
                  value={pincode} onChange={(e) => setPincode(e.target.value)} required data-testid="track-pincode"
                />
              </>
            )}
            <button type="submit" disabled={busy} className="btn-ink disabled:opacity-50" data-testid="track-submit">{busy ? "Checking…" : "Track"}</button>
          </form>
          {error && <p className="text-sm text-red-600 mt-3" data-testid="track-error">{error}</p>}
          {data?.shipments?.map((s) => <Shipment key={s.awb} s={s} />)}
          {data?.orders?.map((o) => (
            <div key={o.order_number} className="mt-6 pt-5 border-t border-black/5" data-testid={`track-order-${o.order_number}`}>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-mono text-sm tracking-wider">{o.order_number}</p>
                <p className="text-xs uppercase tracking-[0.18em] px-2 py-1 border border-neutral-300 text-neutral-700 bg-neutral-50">
                  {o.payment_status === "paid" ? "Confirmed — preparing to ship" : "Order received"}
                </p>
              </div>
              <p className="text-xs text-neutral-500 mt-1">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}</p>
              <p className="text-sm text-neutral-600 mt-2">
                {o.payment_status === "paid"
                  ? "Your parcel is being packed. Tracking appears here as soon as it is handed to the courier."
                  : "We'll confirm your order on WhatsApp. Tracking appears here once it ships."}
              </p>
            </div>
          ))}
        </div>
        <p className="text-sm text-neutral-600 mt-6">
          Need to return something? <Link to="/returns" className="underline underline-offset-4">Start a return</Link> ·{" "}
          <a className="underline underline-offset-4" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">Message us on WhatsApp</a>
        </p>
      </div>
    </>
  );
};

export default TrackOrder;
